/** Headings of the palette, in display order. */
export const PALETTE_GROUPS = ['recent', 'pages', 'actions', 'preferences', 'account'] as const;
export type PaletteGroup = (typeof PALETTE_GROUPS)[number];

/** Something the palette can do, contributed by the part of the app that owns it. */
export interface PaletteCommand {
  /** Stable id (remembered for "Recent"). */
  id: string;
  group: Exclude<PaletteGroup, 'recent'>;
  /** i18n key, or plain text when `literalTitle` is set (e.g. language names). */
  title: string;
  literalTitle?: boolean;
  /** i18n keys shown as a muted path after the title (e.g. a page's section and group). */
  hint?: string[];
  icon?: string;
  /** Extra search terms, matched but not shown. */
  keywords?: string[];
  /** Marks the current page / language. */
  current?: boolean;
  /** Keyboard shortcut doing the same, shown on the right (e.g. "Ctrl B"). */
  shortcut?: string;
  run(): void;
}

/** A command with its texts translated, ready to search and render. */
export interface ResolvedCommand {
  command: PaletteCommand;
  title: string;
  hint: string;
  /** Normalised title, and everything searchable. */
  normalizedTitle: string;
  haystack: string;
}

/** Lower case without accents, so "gestion" finds "Gestión". */
export function normalizeSearchText(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

export function resolveCommand(command: PaletteCommand, translate: (key: string) => string): ResolvedCommand {
  const title = command.literalTitle ? command.title : translate(command.title);
  const hint = (command.hint ?? []).map(translate).join(' › ');
  const normalizedTitle = normalizeSearchText(title);
  return {
    command,
    title,
    hint,
    normalizedTitle,
    haystack: normalizeSearchText([title, hint, ...(command.keywords ?? [])].join(' ')),
  };
}

/**
 * How well a command matches the query (higher is better), or -1 when it does not.
 * Every word of the query must appear somewhere; the title is preferred over the path and keywords,
 * and a match at the start of the title or of one of its words over one in the middle.
 */
export function scoreCommand(item: ResolvedCommand, query: string): number {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return 0;

  const words = normalizedQuery.split(/\s+/);
  if (!words.every((word) => item.haystack.includes(word))) return -1;

  const title = item.normalizedTitle;
  if (title === normalizedQuery) return 100;
  if (title.startsWith(normalizedQuery)) return 80;
  if (title.split(/[\s/›-]+/).some((word) => word.startsWith(normalizedQuery))) return 60;
  if (title.includes(normalizedQuery)) return 40;
  if (words.every((word) => title.includes(word))) return 30;
  return 10;
}

/** Matching commands, best first; equal scores keep their original order. */
export function searchCommands(items: ResolvedCommand[], query: string): ResolvedCommand[] {
  return items
    .map((item, index) => ({ item, index, score: scoreCommand(item, query) }))
    .filter(({ score }) => score >= 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(({ item }) => item);
}
